'use client'

import { usePathname } from 'next/navigation'
import Link from 'next/link'
import { cn } from '@/lib/utils'
import { BodyText, DetailText, SettingsIcon } from 'design-configuration/components'
import { useState } from 'react'
import { shortenWalletAddress } from '@utils/artists'
import { useIsProfileClaimed, useIsProfileOwner, useUserDisplayName, useWalletAddresses } from './profile-context'
import { userProfileRoute } from '@utils/routes'

type NavItem = {
  name: string
  href: string
}

type ProfileNavLinkProps = {
  item: NavItem
  onClick?: () => void
  isHorizontal?: boolean
  isDropdownItem?: boolean
  iconLeft?: React.ReactNode
}

function ProfileNavLink({
  item,
  onClick,
  isHorizontal = false,
  isDropdownItem = false,
  iconLeft
}: ProfileNavLinkProps): React.ReactElement {
  const pathname = usePathname()
  // Use exact matching for the overview page, prefix matching for section pages
  const isOverviewPage = item.name === 'Overview'
  const isActive = isOverviewPage ? pathname === item.href : pathname.startsWith(item.href)

  return (
    <Link
      href={item.href}
      className={cn(
        isActive ? 'font-medium' : 'text-on-surface-variant hover:text-on-surface font-normal transition-colors',
        isHorizontal && 'flex-1 text-center',
        isDropdownItem && 'hover:bg-surface-container px-sm py-xs block w-full text-left',
        iconLeft && 'flex items-center'
      )}
      onClick={onClick}
    >
      {iconLeft && <div className="mr-xs">{iconLeft}</div>}
      <BodyText>{item.name}</BodyText>
    </Link>
  )
}

type MoreDropdownProps = {
  items: NavItem[]
  isOpen: boolean
  onToggle: () => void
  onItemClick: () => void
}

function MoreDropdown({ items, isOpen, onToggle, onItemClick }: MoreDropdownProps): React.ReactElement | null {
  if (items.length === 0) return null

  return (
    <div className="relative flex-1 text-center">
      <button onClick={onToggle} className="text-on-surface-variant w-full">
        <BodyText>More +</BodyText>
      </button>
      {isOpen && (
        <div className="border-outline-variant bg-surface absolute right-0 top-full z-10 mt-1 min-w-[160px] rounded-md border shadow-lg">
          <div className="py-1">
            {items.map((item) => (
              <ProfileNavLink key={item.name} item={item} onClick={onItemClick} isDropdownItem />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

type ProfileNavigationProps = {
  walletOrUsername: string
}

export function ProfileNavigation({ walletOrUsername }: ProfileNavigationProps): React.ReactElement {
  const [moreDropdownOpen, setMoreDropdownOpen] = useState(false)
  const userDisplayName = useUserDisplayName()
  const walletAddresses = useWalletAddresses()
  const isProfileOwner = useIsProfileOwner()
  const isProfileClaimed = useIsProfileClaimed()

  // TODO: Restrict options based on session - need Privy details and more user details in session i think

  const navigationItems: NavItem[] = [
    { name: 'Overview', href: `/profile/${walletOrUsername}` }
    // { name: 'Sets', href: `/profile/${walletOrUsername}/sets` },
    // { name: 'Achievements', href: `/profile/${walletOrUsername}/achievements` }
  ]
  if (isProfileClaimed) {
    navigationItems.push({ name: 'Sets', href: `/profile/${walletOrUsername}/sets` })
    navigationItems.push({ name: 'Achievements', href: `/profile/${walletOrUsername}/achievements` })
  }

  const handleDropdownItemClick = () => setMoreDropdownOpen(false)

  // Split items for different screen sizes
  const smallScreenItems = navigationItems.slice(0, 3)
  const smallScreenMoreItems = navigationItems.slice(3)
  const mediumScreenItems = navigationItems.slice(0, 6)
  const mediumScreenMoreItems = navigationItems.slice(6)

  return (
    <>
      <div className="border-outline-variant w-full border-b md:w-[216px] md:flex-shrink-0 md:border-b-0">
        <nav className="p-md md:h-[calc(100vh-90px)]">
          {/* Horizontal Navigation (below md) */}
          <div className="md:hidden">
            {/* Very small screens (below sm) - 3 items */}
            <div className="flex items-center justify-between sm:hidden">
              {smallScreenItems.map((item) => (
                <ProfileNavLink key={item.name} item={item} isHorizontal />
              ))}
              <MoreDropdown
                items={smallScreenMoreItems}
                isOpen={moreDropdownOpen}
                onToggle={() => setMoreDropdownOpen(!moreDropdownOpen)}
                onItemClick={handleDropdownItemClick}
              />
            </div>

            {/* Small to medium screens (sm to md) - 6 items */}
            <div className="hidden items-center justify-between sm:flex">
              {mediumScreenItems.map((item) => (
                <ProfileNavLink key={item.name} item={item} isHorizontal />
              ))}
              <MoreDropdown
                items={mediumScreenMoreItems}
                isOpen={moreDropdownOpen}
                onToggle={() => setMoreDropdownOpen(!moreDropdownOpen)}
                onItemClick={handleDropdownItemClick}
              />
            </div>
          </div>

          {/* Vertical Sidebar Navigation (md and above) */}
          <div className="gap-md hidden h-full flex-col justify-between md:flex">
            <div className="gap-md flex flex-col">
              <div>
                <div className="flex flex-col">
                  <DetailText className="text-on-surface-variant font-medium uppercase">Collector</DetailText>
                  <Link href={userProfileRoute(walletOrUsername)}>
                    <BodyText className="font-medium hover:opacity-75">
                      {userDisplayName
                        ? userDisplayName
                        : walletAddresses.length > 0
                          ? shortenWalletAddress(walletAddresses[0])
                          : ''}
                    </BodyText>
                  </Link>
                </div>
              </div>
              <div className="bg-outline-variant h-[1px] w-full"></div>
              <ul className="gap-sm flex flex-col">
                {navigationItems.map((item) => (
                  <li key={item.name}>
                    <ProfileNavLink item={item} />
                  </li>
                ))}
              </ul>
            </div>
            {isProfileOwner && (
              <ProfileNavLink iconLeft={<SettingsIcon />} item={{ name: 'Settings', href: '/profile/settings' }} />
            )}
          </div>
        </nav>
      </div>

      {/* Click outside to close dropdown */}
      {moreDropdownOpen ? (
        <div className="fixed inset-0 z-0 md:hidden" onClick={() => setMoreDropdownOpen(false)} />
      ) : null}
    </>
  )
}
